import { t, Trans } from "@lingui/macro";
import { ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useSWR from "swr";
import type { Hash } from "viem";

import {
  REFERRAL_CODE_KEY,
  getReferralWelcomeModalDismissedKey,
} from "config/localStorage";
import { useAuth } from "context/AuthContext/AuthProvider";
import { getReferralCodeOwner, useUserReferralCode } from "domain/referrals/hooks";
import { getReferralCodeLookup, getTraderDetail, upsertTrader } from "lib/apis/referral";
import { helperToast } from "lib/helperToast";
import { isAddressZero, MAX_REFERRAL_CODE_LENGTH, shortenAddress } from "lib/legacy";
import useWallet from "lib/wallets/useWallet";
import { decodeReferralCode, encodeReferralCode } from "sdk/utils/referrals";

import Button from "components/Button/Button";
import Checkbox from "components/Checkbox/Checkbox";
import Modal from "components/Modal/Modal";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";

import "./ReferralJoinModal.css";

export function ReferralJoinModal() {
  const { account, active, signer, chainId, connector } = useWallet();
  const { isAuthenticated, signIn } = useAuth();
  const currentChainId = chainId ?? 0;
  const { search, hash } = useLocation();
  const normalizedAccount = useMemo(
    () => (account && ethers.isAddress(account) ? ethers.getAddress(account) : undefined),
    [account]
  );

  const [isVisible, setIsVisible] = useState(false);
  const [allowAttach, setAllowAttach] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const [localStorageCode, setLocalStorageCode] = useState<string | undefined>(undefined);

  // Ensure we react to newly opened invite links that set local storage ref code.
  useEffect(() => {
    const encodedCode = window.localStorage.getItem(REFERRAL_CODE_KEY);
    if (!encodedCode || !encodedCode.startsWith("0x")) {
      setLocalStorageCode(undefined);
      return;
    }

    try {
      const decodedCode = decodeReferralCode(encodedCode as Hash);
      if (decodedCode && decodedCode.length <= MAX_REFERRAL_CODE_LENGTH) {
        setLocalStorageCode(decodedCode);
        return;
      }
    } catch (_error) {
      // Ignore invalid referral payloads.
    }

    setLocalStorageCode(undefined);
  }, [search, hash, active, account, currentChainId]);

  const { attachedOnChain, userReferralCodeString, localStorageCodeOwner, isReferralCodeStateResolved } =
    useUserReferralCode(signer, currentChainId, account);

  const referralCode = useMemo(
    () => (attachedOnChain ? undefined : userReferralCodeString ?? localStorageCode),
    [attachedOnChain, localStorageCode, userReferralCodeString]
  );

  const dismissKey = useMemo(
    () => getReferralWelcomeModalDismissedKey(currentChainId, account, referralCode),
    [account, currentChainId, referralCode]
  );

  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(window.localStorage.getItem(dismissKey) === "1");
  }, [dismissKey]);

  const shouldLookup = Boolean(referralCode && currentChainId > 0);
  const shouldFetchTraderDetail = Boolean(normalizedAccount && active);

  const { data: traderDetail, error: traderDetailError } = useSWR(
    shouldFetchTraderDetail ? ["referrals:traderDetail:modal", normalizedAccount] : null,
    () => getTraderDetail(normalizedAccount!),
    { dedupingInterval: 30_000, revalidateOnFocus: false }
  );

  const { data: referralLookup, error: referralLookupError } = useSWR(
    shouldLookup ? ["referrals:codeLookup:modal", currentChainId, referralCode?.toLowerCase()] : null,
    () => getReferralCodeLookup(referralCode!, { chainId: currentChainId }),
    { dedupingInterval: 30_000, revalidateOnFocus: false }
  );

  const inviterAddress = useMemo(() => {
    const candidate = referralLookup?.referrer || referralLookup?.owner;
    if (!candidate || !ethers.isAddress(candidate)) return undefined;
    return ethers.getAddress(candidate);
  }, [referralLookup?.owner, referralLookup?.referrer]);

  const isSelfReferral = useMemo(() => {
    if (!normalizedAccount) {
      return false;
    }

    const candidateAddresses = [inviterAddress, localStorageCodeOwner].filter((candidate): candidate is string =>
      Boolean(candidate && ethers.isAddress(candidate))
    );

    return candidateAddresses.some((candidate) => ethers.getAddress(candidate) === normalizedAccount);
  }, [inviterAddress, localStorageCodeOwner, normalizedAccount]);

  const _discountPercent = useMemo(() => {
    if (!referralLookup?.discountShareBps) return undefined;
    const discountBps = Number(referralLookup.discountShareBps);
    if (!Number.isFinite(discountBps)) return undefined;
    return discountBps / 100;
  }, [referralLookup?.discountShareBps]);

  const walletName = (connector as any)?.rkDetails?.name || connector?.name;
  const isMetaMaskConnector = String(walletName ?? "")
    .toLowerCase()
    .includes("metamask");

  const isReferralLookupResolved = !shouldLookup || referralLookup !== undefined || referralLookupError !== undefined;
  const hasValidReferralLookup = !shouldLookup || referralLookup?.exists === true;
  const isTraderDetailResolved =
    !shouldFetchTraderDetail || traderDetail !== undefined || traderDetailError !== undefined;
  const isAlreadyReferred = Boolean(traderDetail?.referred_by);

  const shouldShow = Boolean(
    active &&
      normalizedAccount &&
      currentChainId > 0 &&
      referralCode &&
      isReferralCodeStateResolved &&
      isReferralLookupResolved &&
      isTraderDetailResolved &&
      hasValidReferralLookup &&
      !isAlreadyReferred &&
      !isSelfReferral &&
      !attachedOnChain &&
      !dismissed
  );

  useEffect(() => {
    setIsVisible(shouldShow);
  }, [shouldShow]);

  const dismissModal = () => {
    if (dismissKey) {
      window.localStorage.setItem(dismissKey, "1");
    }
    setDismissed(true);
    setIsVisible(false);
  };

  const onConfirm = async () => {
    if (!allowAttach || !referralCode || !normalizedAccount || isSubmitting) {
      return;
    }

    let resolvedInviterAddress = inviterAddress;
    if (!resolvedInviterAddress) {
      try {
        const onChainOwner = await getReferralCodeOwner(currentChainId, encodeReferralCode(referralCode));
        if (onChainOwner && !isAddressZero(onChainOwner)) {
          resolvedInviterAddress = ethers.getAddress(onChainOwner);
        }
      } catch (_ownerLookupError) {
        // Ignore owner lookup failures and rely on backend validation.
      }
    }

    const isSelfReferralByOwnerLookup =
      resolvedInviterAddress &&
      ethers.isAddress(resolvedInviterAddress) &&
      ethers.getAddress(resolvedInviterAddress) === normalizedAccount;

    if (isSelfReferral || isSelfReferralByOwnerLookup) {
      helperToast.error(t`You can't use your own referral code.`);
      dismissModal();
      return;
    }

    setIsSubmitting(true);
    try {
      if (!isAuthenticated) {
        const session = await signIn();
        if (!session) {
          return;
        }
      }

      await upsertTrader({
        address: normalizedAccount,
        referredBy: referralCode,
        referredByAddress: resolvedInviterAddress,
      });

      helperToast.success(t`Referral code linked.`);
      dismissModal();
    } catch (error) {
      helperToast.error(t`Adding referral code failed.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!referralCode) return null;

  return (
    <Modal
      className="ReferralJoinModal"
      contentClassName="ReferralJoinModal-content"
      isVisible={isVisible}
      setIsVisible={dismissModal}
      label={isMetaMaskConnector ? t`MetaMask x NectarLiquid` : t`Join Now`}
    >
      <div className="ReferralJoinModal-body">
        <p className="ReferralJoinModal-subtitle">
          <Trans>Save on fees by using this code.</Trans>
        </p>

        <div className="ReferralJoinModal-codePill">
          <Trans>Join with code: {referralCode}</Trans>
        </div>

        {inviterAddress && (
          <div className="ReferralJoinModal-metaRow">
            <Trans>Invited by</Trans>{" "}
            <TooltipWithPortal
              position="bottom-start"
              handle={<span className="ReferralJoinModal-highlight">{shortenAddress(inviterAddress, 14)}</span>}
              content={<span className="font-medium">{inviterAddress}</span>}
            />
          </div>
        )}

        <div className="ReferralJoinModal-metaRow">
          {/* {discountPercent !== undefined ? (
            <Trans>Estimated discount: {discountPercent.toFixed(2)}%</Trans>
          ) : (
            <Trans>Discount depends on inviter tier</Trans>
          )

          } */}
        </div>

        <div className="ReferralJoinModal-consent">
          <Checkbox isChecked={allowAttach} setIsChecked={setAllowAttach}>
            <Trans>Link this wallet to the referral code for fee discounts.</Trans>
          </Checkbox>
        </div>

        <Button
          variant="primary-action"
          className="ReferralJoinModal-confirmButton"
          onClick={onConfirm}
          disabled={!allowAttach || isSubmitting}
        >
          {isSubmitting ? <Trans>Confirming...</Trans> : <Trans>Confirm</Trans>}
        </Button>
      </div>
    </Modal>
  );
}
